import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Alert from "react-bootstrap/Alert";
import { MdInfoOutline } from "react-icons/md";
import { useRef, useEffect, useState } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import cloneDeep from "lodash/cloneDeep";
import { sendAuthenticatedAsyncRequest } from "services/AsyncRequestService";
import DismissableModal from "components/DismissableModal";
import { getLoggedInUser } from "services/AuthService";
import PERMISSIONS from "rbac/Permissions.json";
import { getErrorMessage, isObjectEmpty } from "services/UtilityService";
import { ErrorCodeKey } from "lookup/ErrorCodes";
import CONTACTMANAGEMENT_CONSTANTS from "lookup/ContactManagementConstants";
import {
  isStringEmptyOrNull,
  compareEmptyOrNull,
} from "services/UtilityService";
import { showNotification } from "services/NotificationService";
import { AgentWorkspaceConstants } from "lookup/AgentWorkspaceConstants";

import { MdModeEdit } from "react-icons/md";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";

const isPrescriberProfileToggleOn = (value) =>
  value === true || value === "true" || value === 1 || value === "1";

const UpdatePrescriberInfo = (props) => {
  const { prescriber, isDisabled } = props;
  const prescriberInfoCloneForInitialValues = prescriber
    ? cloneDeep(prescriber)
    : {};
  const title =
    "Edit Detail for " + prescriberInfoCloneForInitialValues.prescriberName;
  const modalRef = useRef(
    "UpdateInfoModal " +
      (prescriberInfoCloneForInitialValues.prescriberGlobalId || "New")
  );

  /**
   * After HTTP 422 wrong-number: field names that were rejected and their values at submit time.
   * Update stays disabled until every listed field's current value differs from that snapshot.
   */
  const [wrongPhoneBlockSnapshot, setWrongPhoneBlockSnapshot] = useState(null);

  useEffect(() => {
    setWrongPhoneBlockSnapshot(null);
  }, [prescriber?.prescriberInfoUuid, prescriber?.prescriberGlobalId]);

  /** Clears PM-3452 block state whenever the modal is dismissed (X, Cancel, Esc, success). */
  const handlePrescriberEditModalClose = () => {
    setWrongPhoneBlockSnapshot(null);
  };
  // Hide the modal opener button if called programmatically (hideOpenButton prop is true)
  // By default, hideOpenButton is false so other components can use the pencil icon
  const shouldHideButton = props.hideOpenButton === true;
  const modalOpener = shouldHideButton ? null : (
    props.isFromPrescriberProfileList === true ? (
      <OverlayTrigger overlay={<Tooltip>Edit Prescriber</Tooltip>}>
        <MdModeEdit
          size={20}
          className="user-action-button edit-user"
          onClick={() => handleSpecialOpen(modalRef.current.handleShow)}
          data-testid="icon-prescriber-edit"
          style={{ cursor: "pointer" }}
        />
      </OverlayTrigger>
    ) : (
      <Button
        size="sm"
        variant="light"
        key={1}
        disabled={isObjectEmpty(prescriber) || isDisabled}
        onClick={() => handleSpecialOpen(modalRef.current.handleShow)}
        data-testid="btn-prescriber-edit"
      >
        Edit
      </Button>
    )
  );

  const isLoggedInUserSWIMUser = () => {
    return (
      getLoggedInUser()?.authorities?.includes(
        PERMISSIONS.PERM_SWIM_WORKFLOW_ACCESS
      ) ?? false
    );
  };

  const isNotesRequired = (refreshCallback) => {
    if (refreshCallback) {
      return Yup.string()
        .nullable()
        .max(4096, "Max Length: 4096")
        .required("Required!");
    } else {
      return Yup.string()
        .nullable()
        .max(4096, "Max Length: 4096")
        .notRequired();
    }
  };

  const isNotesOnlyUpdate = (values, oldPrescriber) => {
    return (
      (oldPrescriber.prescriberPractice === values.prescriberPractice ||
        compareEmptyOrNull(
          oldPrescriber.prescriberPractice,
          values.prescriberPractice
        )) &&
      (oldPrescriber.prescriberFirstName === values.prescriberFirstName ||
        compareEmptyOrNull(
          oldPrescriber.prescriberFirstName,
          values.prescriberFirstName
        )) &&
      (oldPrescriber.prescriberLastName === values.prescriberLastName ||
        compareEmptyOrNull(
          oldPrescriber.prescriberLastName,
          values.prescriberLastName
        )) &&
      (oldPrescriber.prescriberFax === values.prescriberFax ||
        compareEmptyOrNull(
          oldPrescriber.prescriberFax,
          values.prescriberFax
        )) &&
      (oldPrescriber.prescriberPhone === values.prescriberPhone ||
        compareEmptyOrNull(
          oldPrescriber.prescriberPhone,
          values.prescriberPhone
        )) &&
      (oldPrescriber.prescriberFax2 === values.prescriberFax2 ||
        compareEmptyOrNull(
          oldPrescriber.prescriberFax2,
          values.prescriberFax2
        )) &&
      (oldPrescriber.prescriberEmail === values.prescriberEmail ||
        compareEmptyOrNull(
          oldPrescriber.prescriberEmail,
          values.prescriberEmail
        )) &&
      (oldPrescriber.prescriberPhone2 === values.prescriberPhone2 ||
        compareEmptyOrNull(
          oldPrescriber.prescriberPhone2,
          values.prescriberPhone2
        )) &&
      (oldPrescriber.prescriberAddressLine1 === values.prescriberAddressLine1 ||
        compareEmptyOrNull(
          oldPrescriber.prescriberAddressLine1,
          values.prescriberAddressLine1
        )) &&
      (oldPrescriber.prescriberAddressLine2 === values.prescriberAddressLine2 ||
        compareEmptyOrNull(
          oldPrescriber.prescriberAddressLine2,
          values.prescriberAddressLine2
        )) &&
      (oldPrescriber.prescriberCityName === values.prescriberCityName ||
        compareEmptyOrNull(
          oldPrescriber.prescriberCityName,
          values.prescriberCityName
        )) &&
      (oldPrescriber.prescriberStateAbbreviation ===
        values.prescriberStateAbbreviation ||
        compareEmptyOrNull(
          oldPrescriber.prescriberStateAbbreviation,
          values.prescriberStateAbbreviation
        )) &&
      (oldPrescriber.prescriberZipCode === values.prescriberZipCode ||
        compareEmptyOrNull(
          oldPrescriber.prescriberZipCode,
          values.prescriberZipCode
        )) &&
      (oldPrescriber.prescriberNameCredentialText ===
        values.prescriberNameCredentialText ||
        compareEmptyOrNull(
          oldPrescriber.prescriberNameCredentialText,
          values.prescriberNameCredentialText
        )) &&
      (oldPrescriber.optedOut === values.optedOut ||
        compareEmptyOrNull(oldPrescriber.optedOut, values.optedOut)) &&
      (props.isFromPrescriberProfileList !== true ||
        ((oldPrescriber.prescriberNotAtPractice === values.prescriberNotAtPractice ||
          compareEmptyOrNull(
            oldPrescriber.prescriberNotAtPractice,
            values.prescriberNotAtPractice
          )) &&
          (oldPrescriber.providerRetired === values.providerRetired ||
            compareEmptyOrNull(
              oldPrescriber.providerRetired,
              values.providerRetired
            )))) &&
      !isStringEmptyOrNull(values.prescriberUpdateNotes?.trim())
    );
  };

  const getUpdateInfoFormSchema = (initialPrescriber) => Yup.object().shape({
    prescriberFirstName: Yup.string().max(
      255,
      CONTACTMANAGEMENT_CONSTANTS.fieldMaxLengthValidationPrefix + "255"
    ),
    prescriberLastName: Yup.string().max(
      255,
      CONTACTMANAGEMENT_CONSTANTS.fieldMaxLengthValidationPrefix + "255"
    ),
    prescriberNameCredentialText: Yup.string().max(
      255,
      CONTACTMANAGEMENT_CONSTANTS.fieldMaxLengthValidationPrefix + "255"
    ),
    prescriberPractice: Yup.string().max(
      255,
      CONTACTMANAGEMENT_CONSTANTS.fieldMaxLengthValidationPrefix + "255"
    ).nullable(true),
    prescriberPhone: Yup.string()
      .matches(/^[0-9]*$/, CONTACTMANAGEMENT_CONSTANTS.numberValidationError)
      .max(
        10,
        CONTACTMANAGEMENT_CONSTANTS.fieldMaxLengthValidationPrefix + "10"
      )
      .min(
        10,
        CONTACTMANAGEMENT_CONSTANTS.fieldMinLengthValidationPrefix + "10"
      )
      .nullable(true),
    prescriberPhone2: Yup.string()
      .matches(/^[0-9]*$/, CONTACTMANAGEMENT_CONSTANTS.numberValidationError)
      .max(
        10,
        CONTACTMANAGEMENT_CONSTANTS.fieldMaxLengthValidationPrefix + "10"
      )
      .min(
        10,
        CONTACTMANAGEMENT_CONSTANTS.fieldMinLengthValidationPrefix + "10"
      )
      .nullable(true),
      prescriberFax: Yup.string()
      .matches(/^[0-9]*$/, CONTACTMANAGEMENT_CONSTANTS.numberValidationError)
      .max(
        10,
        CONTACTMANAGEMENT_CONSTANTS.fieldMaxLengthValidationPrefix + "10"
      )
      .min(
        10,
        CONTACTMANAGEMENT_CONSTANTS.fieldMinLengthValidationPrefix + "10"
      )
      .nullable(true)
      .test('fax-or-email', 'Either fax or email is required', function(value) {
        if (isNotesOnlyUpdate(this.parent, initialPrescriber)) return true;
        const { prescriberEmail } = this.parent;
        const faxValue = value && typeof value === 'string' ? value.trim() : '';
        const emailValue = prescriberEmail && typeof prescriberEmail === 'string' ? prescriberEmail.trim() : '';
        return faxValue !== '' || emailValue !== '';
      }),
    prescriberEmail: Yup.string()
      .email("Enter a valid email")
      .max(
        255,
        CONTACTMANAGEMENT_CONSTANTS.fieldMaxLengthValidationPrefix + "255"
      )
      .nullable(true)
      .test('fax-or-email', 'Either fax or email is required', function(value) {
        if (isNotesOnlyUpdate(this.parent, initialPrescriber)) return true;
        const { prescriberFax } = this.parent;
        const faxValue = prescriberFax && typeof prescriberFax === 'string' ? prescriberFax.trim() : '';
        const emailValue = value && typeof value === 'string' ? value.trim() : '';
        return faxValue !== '' || emailValue !== '';
      }),
    prescriberFax2: Yup.string()
      .matches(/^[0-9]*$/, CONTACTMANAGEMENT_CONSTANTS.numberValidationError)
      .max(
        10,
        CONTACTMANAGEMENT_CONSTANTS.fieldMaxLengthValidationPrefix + "10"
      )
      .min(
        10,
        CONTACTMANAGEMENT_CONSTANTS.fieldMinLengthValidationPrefix + "10"
      )
      .nullable(true),
    prescriberAddressLine1: Yup.string()
      .max(
        255,
        CONTACTMANAGEMENT_CONSTANTS.fieldMaxLengthValidationPrefix + "255"
      )
      .nullable(true),
    prescriberAddressLine2: Yup.string()
      .max(
        255,
        CONTACTMANAGEMENT_CONSTANTS.fieldMaxLengthValidationPrefix + "255"
      )
      .nullable(true),
    prescriberCityName: Yup.string()
      .max(
        255,
        CONTACTMANAGEMENT_CONSTANTS.fieldMaxLengthValidationPrefix + "255"
      )
      .nullable(true),
    prescriberZipCode: Yup.string()
      .matches(/^[0-9]*$/, CONTACTMANAGEMENT_CONSTANTS.numberValidationError)
      .matches(
        /^.{5}$|^.{9}$/,
        CONTACTMANAGEMENT_CONSTANTS.fieldLengthValidationPrefix + "5 or 9"
      )
      .nullable(true),
    prescriberNpi: Yup.string()
      .matches(/^[0-9]*$/, CONTACTMANAGEMENT_CONSTANTS.numberValidationError)
      .max(
        10,
        CONTACTMANAGEMENT_CONSTANTS.fieldMaxLengthValidationPrefix + "10"
      )
      .min(
        10,
        CONTACTMANAGEMENT_CONSTANTS.fieldMinLengthValidationPrefix + "10"
      )
      .nullable(true),
    prescriberUpdateNotes: Yup.string()
      .nullable()
      .max(4096, "Max Length: 4096")
      .required("Required!"),
  }, ['prescriberFax', 'prescriberEmail']);

  const UpdateInfoFormSchema = getUpdateInfoFormSchema(prescriber);

  const isFieldRequired = (key) => {
    return UpdateInfoFormSchema.fields[key]
      ? UpdateInfoFormSchema.fields[key].exclusiveTests.required
      : false;
  };

  const isWrongPhoneSubmitBlocked = (values) => {
    if (
      !wrongPhoneBlockSnapshot ||
      !wrongPhoneBlockSnapshot.fields?.length
    ) {
      return false;
    }
    return wrongPhoneBlockSnapshot.fields.every(
      (fieldName) =>
        String(values[fieldName] ?? "") ===
        String(wrongPhoneBlockSnapshot.values[fieldName] ?? "")
    );
  };

  const submitForm = (
    originalValues,
    setSubmitting,
    setValues,
    oldPrescriber,
    closeModal,
    setErrors,
    setTouched
  ) => {

    if (props.isFromPrescriberProfileList === null || props.isFromPrescriberProfileList === undefined) {
      originalValues.prescriberNotAtPractice = null;  // Set it to null if button is hidden
      originalValues.providerRetired = null;
    }
    // it's best to create a clone because params are passed through reference
    originalValues = {
      ...originalValues,
      isFromAW: props.isFromAW ? true : false,
      wfItemId: props.wfItemId,
      activityType: props.activityType
    };
    const values = cloneDeep(originalValues);
    if (
      (!isStringEmptyOrNull(oldPrescriber.prescriberFax) &&
        isStringEmptyOrNull(values.prescriberFax)) ||
      (!isStringEmptyOrNull(oldPrescriber.prescriberPhone) &&
        isStringEmptyOrNull(values.prescriberPhone)) ||
      (!isStringEmptyOrNull(oldPrescriber.prescriberEmail) &&
      isStringEmptyOrNull(values.prescriberEmail))
      ) {
      showNotification({
        title: "Invalid Change Detected!",
        message: `Email, Fax, and phone numbers cannot be empty.`,
        position: "tr",
        type: "warning",
      });
      setSubmitting(false);
      return;
    }

    if (
      (oldPrescriber.prescriberPractice === values.prescriberPractice ||
        compareEmptyOrNull(
          oldPrescriber.prescriberPractice,
          values.prescriberPractice
        )) &&
      (oldPrescriber.prescriberFirstName === values.prescriberFirstName ||
        compareEmptyOrNull(
          oldPrescriber.prescriberFirstName,
          values.prescriberFirstName
        )) &&
      (oldPrescriber.prescriberLastName === values.prescriberLastName ||
        compareEmptyOrNull(
          oldPrescriber.prescriberLastName,
          values.prescriberLastName
        )) &&
      (oldPrescriber.prescriberFax === values.prescriberFax ||
        compareEmptyOrNull(
          oldPrescriber.prescriberFax,
          values.prescriberFax
        )) &&
      (oldPrescriber.prescriberPhone === values.prescriberPhone ||
        compareEmptyOrNull(
          oldPrescriber.prescriberPhone,
          values.prescriberPhone
        )) &&
      (oldPrescriber.prescriberFax2 === values.prescriberFax2 ||
        compareEmptyOrNull(
          oldPrescriber.prescriberFax2,
          values.prescriberFax2
        )) &&
      (oldPrescriber.prescriberEmail === values.prescriberEmail ||
        compareEmptyOrNull(
          oldPrescriber.prescriberEmail,
          values.prescriberEmail
        )) &&
      (oldPrescriber.prescriberPhone2 === values.prescriberPhone2 ||
        compareEmptyOrNull(
          oldPrescriber.prescriberPhone2,
          values.prescriberPhone2
        )) &&
      (oldPrescriber.prescriberAddressLine1 === values.prescriberAddressLine1 ||
        compareEmptyOrNull(
          oldPrescriber.prescriberAddressLine1,
          values.prescriberAddressLine1
        )) &&
      (oldPrescriber.prescriberAddressLine2 === values.prescriberAddressLine2 ||
        compareEmptyOrNull(
          oldPrescriber.prescriberAddressLine2,
          values.prescriberAddressLine2
        )) &&
      (oldPrescriber.prescriberCityName === values.prescriberCityName ||
        compareEmptyOrNull(
          oldPrescriber.prescriberCityName,
          values.prescriberCityName
        )) &&
      (oldPrescriber.prescriberStateAbbreviation ===
        values.prescriberStateAbbreviation ||
        compareEmptyOrNull(
          oldPrescriber.prescriberStateAbbreviation,
          values.prescriberStateAbbreviation
        )) &&
      (oldPrescriber.prescriberZipCode === values.prescriberZipCode ||
        compareEmptyOrNull(
          oldPrescriber.prescriberZipCode,
          values.prescriberZipCode
        )) &&
      (oldPrescriber.prescriberNameCredentialText ===
        values.prescriberNameCredentialText ||
        compareEmptyOrNull(
          oldPrescriber.prescriberNameCredentialText,
          values.prescriberNameCredentialText
        )) &&
      (oldPrescriber.optedOut === values.optedOut ||
        compareEmptyOrNull(oldPrescriber.optedOut, values.optedOut)) &&
      (props.isFromPrescriberProfileList !== true ||
        (oldPrescriber.prescriberNotAtPractice === values.prescriberNotAtPractice ||
          compareEmptyOrNull(oldPrescriber.prescriberNotAtPractice, values.prescriberNotAtPractice)) &&
        (oldPrescriber.providerRetired === values.providerRetired ||
          compareEmptyOrNull(oldPrescriber.providerRetired, values.providerRetired)))
        && isStringEmptyOrNull(values.prescriberUpdateNotes?.trim())
    ) {
      showNotification({
        title: "No Change Detected!",
        message: `You are trying to save without making any update.`,
        position: "tr",
        type: "warning",
      });
      setSubmitting(false);
      return;
    }

    if (isStringEmptyOrNull(values.prescriberUpdateNotes?.trim())) {
      showNotification({
        title: "Invalid Note!",
        message: `You are trying to save an empty note.`,
        position: "tr",
        type: "warning",
      });
      setSubmitting(false);
      return;
    }

    if (isNotesOnlyUpdate(values, oldPrescriber)) {
      const notePayload = {
        note: values.prescriberUpdateNotes.trim(),
        source: props.isFromAW ? "AW" : "SWIM",
        ...(props.isFromAW && props.activityType && { activityType: props.activityType })
      };
      const noteUrl = `/contact-management/prescriber-info/${oldPrescriber.prescriberInfoUuid}/note${
        props.isFromAW && props.wfItemId ? `?wfItemId=${props.wfItemId}` : ""
      }`;
      sendAuthenticatedAsyncRequest(
        noteUrl,
        "POST",
        notePayload,
        (r) => {
          setSubmitting(false);
          closeModal();
          if (props.onSubmission && typeof props.onSubmission === "function") {
            props.onSubmission(oldPrescriber);
          }
          showNotification({
            title: "Successful Update!",
            message: "Prescriber Info updated successfully",
            position: "tr",
            type: "success",
          });
          props.updateData();
        },
        (r) => {
          setSubmitting(false);
          setValues(oldPrescriber);
          showNotification({
            title: "Something went wrong!",
            message:
              getErrorMessage(r.data) ||
              getErrorMessage("FAILED_TO_UPDATE_PRESCRIBER_INFO"),
            position: "tr",
            type: "error",
          });
        }
      );
      return;
    }

    sendAuthenticatedAsyncRequest(
      `/contact-management/prescriber-info/${oldPrescriber.prescriberInfoUuid}`,
      "PUT",
      values,
      (r) => {
        setSubmitting(false);
        const updatePrescriberInfoDto = r.data;
        closeModal();
        if (props.onSubmission && typeof props.onSubmission === "function") {
          props.onSubmission(updatePrescriberInfoDto);
        }

        // dispatch fax resent notification
        // if (oldPrescriber.prescriberFax !== values.prescriberFax)
        //   showNotification({
        //     title: `Auto Fax Resending Process started!`,
        //     message: `Fax is being resent automatically as the fax number is updated. You dont need to close this activity or other TASK activities of this call group now, they will be closed automatically by the background process. You can go back and work on other call groups.`,
        //     position: "tr",
        //     type: "success",
        //     duration: 20000,
        //   });

        // dispatch success notification
        showNotification({
          title: `Successful Update!`,
          message: `Prescriber Info updated successfully`,
          position: "tr",
          type: "success",
        });

        props.updateData();
      },
      (r) => {
        // PM-3452: When Platform rejects the save because one or both phones were previously
        // marked wrong for this prescriber, surface the per-field messages inline next to the
        // offending input(s) and keep the user's edits intact so they can correct and retry.
        if (
          r?.status === 422 &&
          r?.data?.errorCode === ErrorCodeKey.PHONE_PREVIOUSLY_MARKED_WRONG &&
          r?.data?.fieldErrors &&
          typeof setErrors === "function"
        ) {
          const fieldNames = Object.keys(r.data.fieldErrors);
          setWrongPhoneBlockSnapshot({
            fields: fieldNames,
            values: Object.fromEntries(
              fieldNames.map((k) => [k, values[k] ?? ""])
            ),
          });
          setErrors(r.data.fieldErrors);
          if (typeof setTouched === "function") {
            const touchedFields = Object.keys(r.data.fieldErrors).reduce(
              (acc, field) => ({ ...acc, [field]: true }),
              {}
            );
            setTouched(touchedFields, false);
          }
          setSubmitting(false);
          return;
        }
        setSubmitting(false);
        setValues(oldPrescriber);
        showNotification({
          title: "Something went wrong!",
          message:
            getErrorMessage(r.data) ||
            getErrorMessage("FAILED_TO_UPDATE_PRESCRIBER_INFO"),
          position: "tr",
          type: "error",
        });
      }
    );
  };

  const handleSpecialOpen = (handleRealOpen) => {
    handleRealOpen();
  };

  const onChange = (value, field, setFieldValue) => {
    const numberRegex = /^[0-9\b]+$/;
    value = value.replace(/\s/g, "");

    if (value === "" || numberRegex.test(value)) {
      setFieldValue(field, value);
    }
  };

  const handlePrescriberNotAtPracticeChange = (
    e,
    values,
    resetForm,
    setFieldValue
  ) => {
    const checked = e.target.checked;
    const currentNotes = values.prescriberUpdateNotes ?? "";
    resetForm();
    setFieldValue("prescriberNotAtPractice", checked);
    setFieldValue("prescriberUpdateNotes", currentNotes);
  };

  const handleProviderRetiredChange = (
    e,
    values,
    resetForm,
    setFieldValue
  ) => {
    const checked = e.target.checked;
    const currentNotes = values.prescriberUpdateNotes ?? "";
    resetForm();
    setFieldValue("providerRetired", checked);
    setFieldValue("prescriberUpdateNotes", currentNotes);
  };

  // Auto-open modal when autoOpen prop is true
  useEffect(() => {
    if (props.autoOpen && modalRef.current && modalRef.current.handleShow) {
      modalRef.current.handleShow();
    }
  }, [props.autoOpen]);

  return (
    <DismissableModal
      ref={modalRef}
      openModalButton={modalOpener}
      title={title}
      size="lg"
      preventCloseWhenClickedOutsideModal={true}
      hideFooter={true}
      onClose={handlePrescriberEditModalClose}
    >
      <Container>
        {prescriberInfoCloneForInitialValues.optedOut ? (
          <Alert
            variant="warning"
            style={{ marginBottom: "10px", fontSize: "1.1em" }}
          >
            <MdInfoOutline
              className="icon-displacement-fix"
              style={{ marginRight: "0.25em", fontSize: "1.25em" }}
            />
            This prescriber has opted out of the program.
          </Alert>
        ) : (
          <></>
        )}
        <Formik
        enableReinitialize
          initialValues={{
            prescriberFirstName:
              prescriberInfoCloneForInitialValues.prescriberFirstName || "",
            prescriberLastName:
              prescriberInfoCloneForInitialValues.prescriberLastName || "",
            prescriberNameCredentialText:
              prescriberInfoCloneForInitialValues.prescriberNameCredentialText ||
              "",
            prescriberPractice:
              prescriberInfoCloneForInitialValues.prescriberPractice || "",
            prescriberPhone:
              prescriberInfoCloneForInitialValues.prescriberPhone || "",
            prescriberPhone2:
              prescriberInfoCloneForInitialValues.prescriberPhone2 || "",
            prescriberFax:
              prescriberInfoCloneForInitialValues.prescriberFax || "",
            prescriberFax2:
              prescriberInfoCloneForInitialValues.prescriberFax2 || "",
            prescriberEmail:
              prescriberInfoCloneForInitialValues.prescriberEmail || "",
            prescriberAddressLine1:
              prescriberInfoCloneForInitialValues.prescriberAddressLine1 || "",
            prescriberAddressLine2:
              prescriberInfoCloneForInitialValues.prescriberAddressLine2 || "",
            prescriberCityName:
              prescriberInfoCloneForInitialValues.prescriberCityName || "",
            prescriberStateAbbreviation:
              prescriberInfoCloneForInitialValues.prescriberStateAbbreviation ||
              "",
            prescriberZipCode:
              prescriberInfoCloneForInitialValues.prescriberZipCode || "",
            prescriberNpi:
              prescriberInfoCloneForInitialValues.prescriberNpi || "",
            prescriberUpdateNotes: "", // initially, notes will always be empty
            version: prescriberInfoCloneForInitialValues.version || 0,
            optedOut:
              prescriberInfoCloneForInitialValues.optedOut === null ||
              prescriberInfoCloneForInitialValues.optedOut === undefined
                ? false
                : prescriberInfoCloneForInitialValues.optedOut,
            prescriberNotAtPractice:
              prescriberInfoCloneForInitialValues.prescriberNotAtPractice === null ||
              prescriberInfoCloneForInitialValues.prescriberNotAtPractice === undefined
                ? false
                : prescriberInfoCloneForInitialValues.prescriberNotAtPractice,
            providerRetired:
              prescriberInfoCloneForInitialValues.providerRetired === null ||
              prescriberInfoCloneForInitialValues.providerRetired === undefined
                ? false
                : prescriberInfoCloneForInitialValues.providerRetired,
          }}
          onSubmit={(values, { setSubmitting, setValues, setErrors, setTouched }) => {
            submitForm(
              values,
              setSubmitting,
              setValues,
              prescriber,
              modalRef.current.handleClose,
              setErrors,
              setTouched
            );
          }}
          validationSchema={UpdateInfoFormSchema}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            setFieldValue,
            setValues,
            setErrors,
            resetForm,
          }) => {
            const wrongPhoneSubmitBlocked = isWrongPhoneSubmitBlocked(values);

            const onBlockedPhoneFieldChange = (e, fieldName) => {
              onChange(e.target.value, fieldName, setFieldValue);
              if (
                wrongPhoneBlockSnapshot?.fields?.includes(fieldName) &&
                typeof setErrors === "function"
              ) {
                const next = { ...errors };
                delete next[fieldName];
                setErrors(next);
              }
            };

            return (
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label
                      className={
                        isFieldRequired("prescriberFirstName")
                          ? "rm-required-field"
                          : ""
                      }
                    >
                      Prescriber First Name
                    </Form.Label>
                    <Form.Control
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.prescriberFirstName}
                      name="prescriberFirstName"
                      disabled={true}
                      data-testid="input-prescriber-edit-first-name"
                    />
                    <Form.Text className="text-small text-danger">
                      {errors.prescriberFirstName &&
                        touched.prescriberFirstName &&
                        errors.prescriberFirstName}
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label
                      className={
                        isFieldRequired("prescriberLastName")
                          ? "rm-required-field"
                          : ""
                      }
                    >
                      Prescriber Last Name
                    </Form.Label>
                    <Form.Control
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.prescriberLastName}
                      name="prescriberLastName"
                      disabled={true}
                      data-testid="input-prescriber-edit-last-name"
                    />
                    <Form.Text className="text-small text-danger">
                      {errors.prescriberLastName &&
                        touched.prescriberLastName &&
                        errors.prescriberLastName}
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label
                      className={
                        isFieldRequired("prescriberNameCredentialText")
                          ? "rm-required-field"
                          : ""
                      }
                    >
                      Suffix
                    </Form.Label>
                    <Form.Control
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.prescriberNameCredentialText}
                      name="prescriberNameCredentialText"
                      disabled={
                        props.isFromPrescriberProfileList === true &&
                        (isPrescriberProfileToggleOn(values.prescriberNotAtPractice) ||
                          isPrescriberProfileToggleOn(values.providerRetired))
                      }
                      data-testid="input-prescriber-edit-credentials"
                    />
                    <Form.Text className="text-small text-danger">
                      {errors.prescriberNameCredentialText &&
                        touched.prescriberNameCredentialText &&
                        errors.prescriberNameCredentialText}
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label
                      className={
                        isFieldRequired("prescriberPractice")
                          ? "rm-required-field"
                          : ""
                      }
                    >
                      Practice Name
                    </Form.Label>
                    <Form.Control
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.prescriberPractice}
                      name="prescriberPractice"
                      disabled={
                        props.isFromPrescriberProfileList === true &&
                        (isPrescriberProfileToggleOn(values.prescriberNotAtPractice) ||
                          isPrescriberProfileToggleOn(values.providerRetired))
                      }
                      data-testid="input-prescriber-edit-practice"
                    />
                    <Form.Text className="text-small text-danger">
                      {errors.prescriberPractice &&
                        touched.prescriberPractice &&
                        errors.prescriberPractice}
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label
                      className={
                        isFieldRequired("prescriberAddressLine1")
                          ? "rm-required-field"
                          : ""
                      }
                    >
                      Prescriber Street Address Line 1
                    </Form.Label>
                    <Form.Control
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.prescriberAddressLine1}
                      name="prescriberAddressLine1"
                      disabled={
                        props.isFromPrescriberProfileList === true &&
                        (isPrescriberProfileToggleOn(values.prescriberNotAtPractice) ||
                          isPrescriberProfileToggleOn(values.providerRetired))
                      }
                      data-testid="input-prescriber-edit-address-line-1"
                    />
                    <Form.Text className="text-small text-danger">
                      {errors.prescriberAddressLine1 &&
                        touched.prescriberAddressLine1 &&
                        errors.prescriberAddressLine1}
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label
                      className={
                        isFieldRequired("prescriberPhone")
                          ? "rm-required-field"
                          : ""
                      }
                    >
                      Prescriber Phone
                    </Form.Label>
                    <Form.Control
                      onChange={(e) =>
                        onBlockedPhoneFieldChange(e, "prescriberPhone")
                      }
                      onBlur={handleBlur}
                      value={values.prescriberPhone}
                      name="prescriberPhone"
                      disabled={
                        props.isFromPrescriberProfileList === true &&
                        (isPrescriberProfileToggleOn(values.prescriberNotAtPractice) ||
                          isPrescriberProfileToggleOn(values.providerRetired))
                      }
                      data-testid="input-prescriber-edit-phone"
                    />
                    <Form.Text className="text-small text-danger">
                      {errors.prescriberPhone &&
                        touched.prescriberPhone &&
                        errors.prescriberPhone}
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label
                      className={
                        isFieldRequired("prescriberAddressLine2")
                          ? "rm-required-field"
                          : ""
                      }
                    >
                      Prescriber Street Address Line 2
                    </Form.Label>
                    <Form.Control
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.prescriberAddressLine2}
                      name="prescriberAddressLine2"
                      disabled={
                        props.isFromPrescriberProfileList === true &&
                        (isPrescriberProfileToggleOn(values.prescriberNotAtPractice) ||
                          isPrescriberProfileToggleOn(values.providerRetired))
                      }
                      data-testid="input-prescriber-edit-address-line-2"
                    />
                    <Form.Text className="text-small text-danger">
                      {errors.prescriberAddressLine2 &&
                        touched.prescriberAddressLine2 &&
                        errors.prescriberAddressLine2}
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label
                      className={
                        isFieldRequired("prescriberPhone2")
                          ? "rm-required-field"
                          : ""
                      }
                    >
                      Alternate Prescriber Phone
                    </Form.Label>
                    <Form.Control
                      onChange={(e) =>
                        onBlockedPhoneFieldChange(e, "prescriberPhone2")
                      }
                      onBlur={handleBlur}
                      value={values.prescriberPhone2}
                      name="prescriberPhone2"
                      disabled={
                        props.isFromPrescriberProfileList === true &&
                        (isPrescriberProfileToggleOn(values.prescriberNotAtPractice) ||
                          isPrescriberProfileToggleOn(values.providerRetired))
                      }
                      data-testid="input-prescriber-edit-alt-phone"
                    />
                    <Form.Text className="text-small text-danger">
                      {errors.prescriberPhone2 &&
                        touched.prescriberPhone2 &&
                        errors.prescriberPhone2}
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label
                      className={
                        isFieldRequired("prescriberCityName")
                          ? "rm-required-field"
                          : ""
                      }
                    >
                      City
                    </Form.Label>
                    <Form.Control
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.prescriberCityName}
                      name="prescriberCityName"
                      disabled={
                        props.isFromPrescriberProfileList === true &&
                        (isPrescriberProfileToggleOn(values.prescriberNotAtPractice) ||
                          isPrescriberProfileToggleOn(values.providerRetired))
                      }
                      data-testid="input-prescriber-edit-city-name"
                    />
                    <Form.Text className="text-small text-danger">
                      {errors.prescriberCityName &&
                        touched.prescriberCityName &&
                        errors.prescriberCityName}
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label
                      className={
                        isFieldRequired("prescriberNpi")
                          ? "rm-required-field"
                          : ""
                      }
                    >
                      Prescriber Npi
                    </Form.Label>
                    <Form.Control
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.prescriberNpi}
                      name="prescriberNpi"
                      disabled
                      data-testid="input-prescriber-edit-npi"
                    />
                    <Form.Text className="text-small text-danger">
                      {errors.prescriberNpi &&
                        touched.prescriberNpi &&
                        errors.prescriberNpi}
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label
                      className={
                        isFieldRequired("prescriberStateAbbreviation")
                          ? "rm-required-field"
                          : ""
                      }
                    >
                      State
                    </Form.Label>
                    <Form.Select
                      onChange={handleChange}
                      value={values.prescriberStateAbbreviation}
                      name="prescriberStateAbbreviation"
                      disabled={
                        props.isFromPrescriberProfileList === true &&
                        (isPrescriberProfileToggleOn(values.prescriberNotAtPractice) ||
                          isPrescriberProfileToggleOn(values.providerRetired))
                      }
                      data-testid="input-prescriber-edit-state-name"
                    >
                      {/* Render selected value only if it's not in the dropdown options */}
                      {!AgentWorkspaceConstants.stateAbbreviations.find(
                        (option) =>
                          option.value === values.prescriberStateAbbreviation
                      ) &&
                        values.prescriberStateAbbreviation && (
                          <option value="">
                            {values.prescriberStateAbbreviation}
                          </option>
                        )}
                      {/* Render "None" if no selected value */}
                      {!values.prescriberStateAbbreviation && (
                        <option value="">None</option>
                      )}
                      {/* Render dropdown options */}
                      {AgentWorkspaceConstants.stateAbbreviations.map(
                        (option) => (
                          <option key={option.value} value={option.value}>
                            {option.key}
                          </option>
                        )
                      )}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label
                      className={
                        isFieldRequired("prescriberFax")
                          ? "rm-required-field"
                          : ""
                      }
                    >
                      Prescriber Fax
                    </Form.Label>
                    <Form.Control
                      onChange={(e) =>
                        onChange(e.target.value, "prescriberFax", setFieldValue)
                      }
                      onBlur={handleBlur}
                      value={values.prescriberFax}
                      name="prescriberFax"
                      disabled={
                        props.isFromPrescriberProfileList === true &&
                        (isPrescriberProfileToggleOn(values.prescriberNotAtPractice) ||
                          isPrescriberProfileToggleOn(values.providerRetired))
                      }
                      data-testid="input-prescriber-edit-fax"
                    />
                    <Form.Text className="text-small text-danger">
                      {errors.prescriberFax &&
                        touched.prescriberFax &&
                        errors.prescriberFax}
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label
                      className={
                        isFieldRequired("prescriberZipCode")
                          ? "rm-required-field"
                          : ""
                      }
                    >
                      Zip Code
                    </Form.Label>
                    <Form.Control
                      onChange={(e) =>
                        onChange(
                          e.target.value,
                          "prescriberZipCode",
                          setFieldValue
                        )
                      }
                      onBlur={handleBlur}
                      value={values.prescriberZipCode}
                      name="prescriberZipCode"
                      disabled={
                        props.isFromPrescriberProfileList === true &&
                        (isPrescriberProfileToggleOn(values.prescriberNotAtPractice) ||
                          isPrescriberProfileToggleOn(values.providerRetired))
                      }
                      data-testid="input-prescriber-edit-zip-code"
                    />
                    <Form.Text className="text-small text-danger">
                      {errors.prescriberZipCode &&
                        touched.prescriberZipCode &&
                        errors.prescriberZipCode}
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label
                      className={
                        isFieldRequired("prescriberFax2")
                          ? "rm-required-field"
                          : ""
                      }
                    >
                      Alternate Prescriber Fax
                    </Form.Label>
                    <Form.Control
                      onChange={(e) =>
                        onChange(
                          e.target.value,
                          "prescriberFax2",
                          setFieldValue
                        )
                      }
                      onBlur={handleBlur}
                      value={values.prescriberFax2}
                      name="prescriberFax2"
                      disabled={
                        props.isFromPrescriberProfileList === true &&
                        (isPrescriberProfileToggleOn(values.prescriberNotAtPractice) ||
                          isPrescriberProfileToggleOn(values.providerRetired))
                      }
                      data-testid="input-prescriber-edit-alt-fax"
                    />
                    <Form.Text className="text-small text-danger">
                      {errors.prescriberFax2 &&
                        touched.prescriberFax2 &&
                        errors.prescriberFax2}
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label
                      className={
                        isFieldRequired("prescriberEmail")
                          ? "rm-required-field"
                          : ""
                      }
                    >
                      Prescriber Email
                    </Form.Label>
                    <Form.Control
                      onChange={(e) =>
                        setFieldValue("prescriberEmail", e.target.value)
                      }
                      onBlur={handleBlur}
                      value={values.prescriberEmail}
                      name="prescriberEmail"
                      disabled={
                        props.isFromPrescriberProfileList === true &&
                        (isPrescriberProfileToggleOn(values.prescriberNotAtPractice) ||
                          isPrescriberProfileToggleOn(values.providerRetired))
                      }
                      data-testid="input-prescriber-edit-email"
                    />
                    <Form.Text className="text-small text-danger">
                      {errors.prescriberEmail &&
                        touched.prescriberEmail &&
                        errors.prescriberEmail}
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                      {props.isFromPrescriberProfileList === true && (
                        <Form.Group>
                          <Form.Label
                            className={
                              isFieldRequired("providerRetired") ? "rm-required-field" : ""
                            }
                          >
                            Provider Retired
                          </Form.Label>
                          <Form.Check
                            type="switch"
                            name="providerRetired"
                            id="provider-retired"
                            className="rm-form-switch rm-switch-negative"
                            checked={isPrescriberProfileToggleOn(
                              values.providerRetired
                            )}
                            onChange={(e) =>
                              handleProviderRetiredChange(
                                e,
                                values,
                                resetForm,
                                setFieldValue
                              )
                            }
                            onBlur={handleBlur}
                            disabled={
                              props.isFromPrescriberProfileList === true &&
                              isPrescriberProfileToggleOn(
                                values.prescriberNotAtPractice
                              )
                            }
                            data-testid="input-prescriber-edit-provider-retired"
                          />
                        </Form.Group>
                      )}
                  </Col>
              </Row>
              <Row>
                  <Col md={6}>
                   {isLoggedInUserSWIMUser() && (
                    <Form.Group>
                      <Form.Label
                        className={
                          isFieldRequired("optedOut") ? "rm-required-field" : ""
                        }
                      >
                        Prescriber Opt-out
                      </Form.Label>
                      <Form.Check
                        type="switch"
                        name="optedOut"
                        id="prescriber-opt-out"
                        className="rm-form-switch rm-switch-negative"
                        value={values.optedOut}
                        defaultChecked={values.optedOut}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={
                          props.isFromPrescriberProfileList === true &&
                          (isPrescriberProfileToggleOn(
                            values.prescriberNotAtPractice
                          ) ||
                            isPrescriberProfileToggleOn(values.providerRetired))
                        }
                        data-testid="input-prescriber-edit-opt-out"
                      />
                    </Form.Group>
                    )}
                  </Col>
                    <Col md={6}>
                      {props.isFromPrescriberProfileList === true && (
                        <Form.Group>
                          <Form.Label
                            htmlFor="prescriber-not-at-practice"
                            className={
                              isFieldRequired("prescriberNotAtPractice") ? "rm-required-field" : ""
                            }
                          >
                            Pause Fax Outreach (Not At Practice)
                          </Form.Label>
                          <Form.Check
                            type="switch"
                            name="prescriberNotAtPractice"
                            id="prescriber-not-at-practice"
                            className="form-check rm-form-switch rm-switch-negative"
                            checked={isPrescriberProfileToggleOn(
                              values.prescriberNotAtPractice
                            )}
                            onChange={(e) =>
                              handlePrescriberNotAtPracticeChange(
                                e,
                                values,
                                resetForm,
                                setFieldValue
                              )
                            }
                            onBlur={handleBlur}
                            disabled={
                              props.isFromPrescriberProfileList === true &&
                              isPrescriberProfileToggleOn(values.providerRetired)
                            }
                            data-testid="input-prescriber-edit-prescriber-not-at-practice"
                            aria-describedby="prescriber-not-at-practice-helper"
                          />
                          <div style={{ marginTop: "20px" }}>
                            <Alert
                              id="prescriber-not-at-practice-helper"
                              variant="warning"
                              className="mt-2 mb-0"
                              style={{ fontSize: "1.1em" } }
                            >
                              <div className="d-flex align-items-start">
                                <span>
                                  {
                                    <>
                                      Marks the prescriber as not at practice for fax outreach only. Phone outreach is unaffected and is managed automatically through call-closure outcomes.
                                      <br />
                                      For Fax Number: {values.prescriberFax}
                                    </>
                               
                                  }
                                </span>
                              </div>
                            </Alert>
                          </div>
                        </Form.Group>
                      )}
                  </Col>
              </Row>
              <Row className="big-top-spacer">
                <Row md={12}>
                  <Form.Group style={{ marginTop: "40px" }}>
                    <Form.Label
                      className={
                        isFieldRequired("prescriberUpdateNotes")
                          ? "rm-required-field"
                          : ""
                      }
                    >
                      Notes
                    </Form.Label>
                    <Form.Control
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.prescriberUpdateNotes}
                      as="textarea"
                      name="prescriberUpdateNotes"
                      data-testid="input-prescriber-edit-notes"
                    />
                    <Form.Text className="text-small text-danger">
                      {errors.prescriberUpdateNotes &&
                        touched.prescriberUpdateNotes &&
                        errors.prescriberUpdateNotes}
                    </Form.Text>
                    {wrongPhoneSubmitBlocked && (
                      <Form.Text
                        id="prescriber-wrong-phone-block-footer-hint"
                        className="text-small text-danger d-block mt-2"
                        data-testid="prescriber-wrong-phone-block-footer-hint"
                      >
                        {CONTACTMANAGEMENT_CONSTANTS.wrongPhoneBlockFooterMessage}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Row>
              </Row>
              <Row className="top-spacer">
                <Col md={{ offset: "4", span: "4" }}>
                  <Button
                    className="btn-block"
                    variant="outline-dark"
                    onClick={() => modalRef.current.handleClose()}
                    disabled={isSubmitting}
                    data-testid="btn-prescriber-edit-cancel"
                  >
                    Cancel
                  </Button>
                </Col>
                <Col md={4} className="bottom-spacer">
                  <Button
                    type="submit"
                    variant="success"
                    disabled={
                      isSubmitting || wrongPhoneSubmitBlocked
                    }
                    className="btn-block"
                    data-testid="btn-prescriber-edit-save"
                  >
                    {isSubmitting ? "Updating ... " : "Update"}
                  </Button>
                </Col>
              </Row>
            </Form>
          );
          }}
        </Formik>
      </Container>
    </DismissableModal>
  );
};

export default UpdatePrescriberInfo;
